import React, { createContext, useState, useCallback, useEffect } from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';

// Create toast context
export const ToastContext = createContext({
  showToast: () => {},
  hideToast: () => {},
  toasts: []
});

/**
 * Toast Provider Component
 * Manages global toast notifications
 *
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Child components
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Auto-hide toasts after duration
  useEffect(() => {
    if (toasts.length > 0) {
      const timer = setTimeout(() => {
        // Remove the oldest toast
        setToasts(prevToasts => prevToasts.slice(1));
      }, toasts[0].duration || 5000);

      return () => clearTimeout(timer);
    }
  }, [toasts]);

  // Show a new toast notification
  const showToast = useCallback((message, options = {}) => {
    const { type = 'info', title, duration = 5000 } = options;

    const id = Date.now().toString();

    setToasts(prevToasts => [
      ...prevToasts,
      { id, message, type, title, duration }
    ]);

    return id;
  }, []);

  // Hide a specific toast notification
  const hideToast = useCallback((id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  // Get icon based on toast type
  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'error':
        return <AlertCircle className="text-red-500" size={20} />;
      case 'info':
      default:
        return <Info className="text-blue-500" size={20} />;
    }
  };

  // Get background color based on toast type
  const getBackground = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  // Toast list component
  const ToastList = () => (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-md pointer-events-none">
      <div className="pointer-events-auto">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`p-4 rounded-lg shadow-lg border ${getBackground(toast.type)} animate-enter mb-2`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3">
                {getIcon(toast.type)}
              </div>
              <div className="flex-1">
                {toast.title && (
                  <h4 className="text-sm font-semibold mb-1">
                    {toast.title}
                  </h4>
                )}
                <p className="text-sm text-gray-700">{toast.message}</p>
              </div>
              <div className="ml-3 flex-shrink-0">
                <button
                  onClick={() => hideToast(toast.id)}
                  className="p-1 rounded-full hover:bg-white focus:outline-none"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <ToastContext.Provider value={{ showToast, hideToast, toasts }}>
      {children}
      <ToastList />
    </ToastContext.Provider>
  );
};

export default ToastContext;
